/**
 * @typedef {import('antd').InputProps} InputProps
 */

/**
 * @typedef {Object} CustomInputProps
 * @property {React.ReactNode}  [helperText]  to display the custom helper text below the input box
 * @property {React.ReactNode}  [label] to display the label of the input element.
 * @property {React.ReactNode}  [errorText] to display the error message.
 * @property {number}  [rows] to define the number of rows in a input.
 * @property {Boolean}  [error] to set error.
 * @property {string}  [className] To give custom styling to input element
 * @property {string}  [type]  To display the input in different type. valid value are any input type MDN value along with textarea to render textarea.
 * @property {object}  [classes] To give styling to individual elements
 * @property {string}  [classes.label] To give styling to label element
 * @property {string}  [classes.input] To give styling to input element
 * @property {string}  [classes.helperText]  To give styling to helper text element
 */

import React from 'react';

import { Input as InputCore } from 'antd';
import cx from 'classnames';

import InputLabel from './InputLabel';
import FormHelperText from './FormHelperText';
import styles from './Input.module.scss';

const { TextArea, Password } = InputCore;

/**
 * @description UI Component to display the input text. Underlying it is using the antd input component.
 *
 * @author  Hemant Kumar Goyal <@hemantkumar.goyal>
 *
 * @reference https://ant.design/components/input
 *
 * @param { InputProps & CustomInputProps } props https://ant.design/components/input
 *
 * @returns {React.Element}
 */

const Input = (props) => {
	const {
		label = null,
		id,
		helperText,
		required,
		classes = {},
		className,
		error,
		errorText = null,
		inputRef,
	} = props;

	const _renderInputCore = () => {
		const { type = 'text' } = props;

		if (type === 'textarea') {
			return (
				<TextArea
					{...props}
					ref={inputRef}
					className={cx(classes.input, {
						[styles.error]: error,
					})}
				/>
			);
		} else if (type === 'password') {
			return (
				<Password
					{...props}
					ref={inputRef}
					className={cx(classes.input, {
						[styles.error]: error,
					})}
				/>
			);
		}

		return (
			<InputCore
				{...props}
				ref={inputRef}
				className={cx(classes.input, styles.input, {
					[styles.error]: error,
				})}
			/>
		);
	};

	return (
		<div className={cx(className, styles.inputWrapper)}>
			{label ? (
				<InputLabel htmlFor={id} required={required} className={classes.label}>
					{label}
				</InputLabel>
			) : null}
			{_renderInputCore()}
			{helperText || errorText ? (
				<FormHelperText className={classes.helperText} error={error}>
					{error && errorText ? errorText : helperText}
				</FormHelperText>
			) : null}
		</div>
	);
};

export default Input;
